import React from 'react';
import { ReactSortable } from 'react-sortablejs';
import {
  Box,
  Collapse,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  useFetchCampaignSteps,
  useReferenceObjectAdmin,
} from '../../../../hooks/campaign-hooks';
import { Campaign, CampaignWorkshopTeamRole } from '../../../../support/campaigns';
import { GhostButton, SectionHeader, builderStyles } from './primitives';
import { byOrder, nextOrderOf, signature } from './builderShared';
import { buildReferenceUsage, WhereUsed } from './referenceUsage';

/**
 * Admin editor for the Cisco-side direct-team roles a campaign's workshop can be staffed from
 * (`CampaignWorkshopTeamRole`) — the data-driven replacement for the guided runner's static
 * `TEAM_ROLES` literal.
 *
 * Rows are not edited inline: each shows a read-only summary and an Edit control that expands its
 * config plus a "where it's used" section listing the steps (prep sections) that offer this role.
 * Same full-record PUT persistence as the rest of the builder; drag order persists to `order`.
 */
export function TeamRolesEditor(props: { campaign: Campaign | null; canWrite: boolean }): JSX.Element {
  const { campaign, canWrite } = props;

  // Global (library) mode = no campaign: manage every global team role directly (create/edit/delete).
  // Campaign mode = a campaign references a subset: `admin.linked` is that subset, `admin.unlinked`
  // backs the "attach existing" picker, and `createLinked`/`detach` create-and-link / detach without
  // deleting the (possibly shared) global role.
  const global = campaign == null;
  const admin = useReferenceObjectAdmin<CampaignWorkshopTeamRole>('teamRole', campaign?.id);
  const rows = global ? admin.all : admin.linked;
  const isLoading = admin.isLoading;
  const { data: steps = [] } = useFetchCampaignSteps(campaign?.id);
  const mut = admin;

  const [attachAnchor, setAttachAnchor] = React.useState<null | HTMLElement>(null);
  const [roles, setRoles] = React.useState<CampaignWorkshopTeamRole[]>([]);
  const sig = signature(rows);
  React.useEffect(() => {
    setRoles(byOrder(rows));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const [expanded, setExpanded] = React.useState<Set<number>>(new Set());
  const toggleExpanded = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const stepsSig = signature(steps);
  const usage = React.useMemo(
    () => buildReferenceUsage(steps),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stepsSig],
  );

  const patchLocal = (id: number, next: Partial<CampaignWorkshopTeamRole>) =>
    setRoles((prev) => prev.map((r) => (r.id === id ? { ...r, ...next } : r)));

  const save = (row: CampaignWorkshopTeamRole, next: Partial<CampaignWorkshopTeamRole>) => {
    patchLocal(row.id, next);
    mut.update.mutate({ ...row, ...next, id: row.id });
  };

  const add = () => {
    const fields = {
      name: '',
      order: nextOrderOf(roles),
      hidden: false,
    };
    // Global mode creates a bare global role; campaign mode creates-and-links it to the campaign.
    if (global) void admin.create(fields);
    else void admin.createLinked(fields);
  };

  const attach = (id: number) => admin.attachExisting(id);

  // Campaign mode: "Remove" detaches this campaign's reference; the global role (which another
  // campaign may reference) is left intact. Global mode: delete the global role everywhere (with
  // confirmation, since it is removed from every campaign that references it).
  const remove = (row: CampaignWorkshopTeamRole) => {
    if (global) {
      const ok = window.confirm(
        `Delete “${row.name.trim() || 'this role'}” from the global library? It will be removed from every campaign that references it.`,
      );
      if (!ok) return;
      setRoles((prev) => prev.filter((r) => r.id !== row.id));
      admin.removeGlobal(row.id);
      return;
    }
    setRoles((prev) => prev.filter((r) => r.id !== row.id));
    admin.detach(row.id);
  };

  const reorder = (nextList: CampaignWorkshopTeamRole[]) => {
    setRoles(nextList);
    nextList.forEach((row, index) => {
      if ((row.order ?? 0) !== index) mut.update.mutate({ ...row, order: index, id: row.id });
    });
  };

  return (
    <div className={builderStyles.contentCard}>
      <SectionHeader
        compact
        title="Workshop team roles"
        subtitle="The Cisco-side roles this campaign's workshop can be staffed from."
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <>
          {roles.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No team roles yet.
            </Typography>
          )}

          <ReactSortable
            list={roles.map((r) => ({ ...r }))}
            setList={(next) => canWrite && reorder(next as CampaignWorkshopTeamRole[])}
            handle=".cb-role-drag-handle"
            animation={150}
            disabled={!canWrite}
          >
            {roles.map((row) => {
              const isOpen = expanded.has(row.id);
              const usedIn = usage.teamRoles.get(row.name) ?? [];
              return (
                <Box
                  key={row.id}
                  sx={{
                    border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: 1,
                    p: 1,
                    mb: 1,
                    opacity: row.hidden ? 0.55 : 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      className="cb-role-drag-handle"
                      sx={{ cursor: canWrite ? 'grab' : 'default', color: 'text.disabled', display: 'flex' }}
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.name.trim() || <em>(untitled role)</em>}
                      </Typography>
                      {global ? (
                        <Typography variant="caption" color="text.secondary">
                          used by {admin.usageCount(row.id)} campaign
                          {admin.usageCount(row.id) === 1 ? '' : 's'}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          used in {usedIn.length} step{usedIn.length === 1 ? '' : 's'}
                        </Typography>
                      )}
                    </Box>
                    {canWrite && (
                      <Stack direction="row">
                        <Tooltip title={isOpen ? 'Close' : 'Edit'}>
                          <IconButton size="small" onClick={() => toggleExpanded(row.id)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={row.hidden ? 'Hidden from the runner — show' : 'Hide from the runner'}>
                          <IconButton size="small" onClick={() => save(row, { hidden: !row.hidden })}>
                            {row.hidden ? (
                              <VisibilityOffIcon fontSize="small" />
                            ) : (
                              <VisibilityIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={global ? 'Delete team role (removes it from all campaigns)' : 'Remove team role'}>
                          <IconButton size="small" onClick={() => remove(row)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>

                  <Collapse in={isOpen} unmountOnExit>
                    <Stack spacing={1} sx={{ mt: 1.25 }}>
                      <TextField
                        label="Role"
                        size="small"
                        fullWidth
                        value={row.name}
                        disabled={!canWrite}
                        onChange={(e) => patchLocal(row.id, { name: e.target.value })}
                        onBlur={() => save(row, { name: row.name })}
                      />
                      <TextField
                        label="What they do in the workshop"
                        size="small"
                        fullWidth
                        multiline
                        value={row.description ?? ''}
                        disabled={!canWrite}
                        onChange={(e) => patchLocal(row.id, { description: e.target.value })}
                        onBlur={() => save(row, { description: row.description })}
                      />
                      {!global && <WhereUsed steps={usedIn} noun="role" />}
                    </Stack>
                  </Collapse>
                </Box>
              );
            })}
          </ReactSortable>

          {canWrite && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <GhostButton onClick={add} disabled={mut.isSaving}>
                + Add role
              </GhostButton>
              {/* "Attach existing" only makes sense inside a campaign; the global library shows all. */}
              {!global && (
                <>
                  <GhostButton
                    onClick={(e) => setAttachAnchor(e.currentTarget)}
                    disabled={mut.isSaving || admin.unlinked.length === 0}
                  >
                    Add existing
                  </GhostButton>
                  <Menu
                    anchorEl={attachAnchor}
                    open={Boolean(attachAnchor)}
                    onClose={() => setAttachAnchor(null)}
                  >
                    {admin.unlinked.map((r) => (
                      <MenuItem
                        key={r.id}
                        onClick={() => {
                          attach(r.id);
                          setAttachAnchor(null);
                        }}
                      >
                        {r.name.trim() || `(untitled role #${r.id})`}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Stack>
          )}
        </>
      )}
    </div>
  );
}

export default TeamRolesEditor;
